/**
 * Search by meaning (R5-15): hybrid keyword + vector search on the job collections, using a
 * Typesense BUILT-IN multilingual embedding model so no paid embedding API is ever called. The
 * model runs inside Typesense; documents get their vector when indexed and queries get theirs when
 * searched. Pure builders only; the schema field is added by the opt-in migration
 * (utils/typesense/migrations/job-semantic-embedding-migration.ts), never by the deploy.
 *
 * Everything here is dark until `isSemanticSearchEnabled()` says so (SEMANTIC_SEARCH_ENABLED=true on
 * the function that serves it; never in the shared env file) and the field exists.
 */

export const SEMANTIC_EMBEDDING_FIELD = "embedding";
/** Typesense built-in multilingual model (Finnish, Swedish and English share one vector space). Check it exists in the running Typesense version before migrating. */
export const SEMANTIC_DEFAULT_MODEL = "ts/multilingual-e5-small";
/** What a job is embedded from: short, language-bearing text only (no description: cheaper and less noisy). */
export const SEMANTIC_EMBED_FROM = ["title", "normalisedTitle", "skillsAndTechnologies"] as const;
/** Weight of the vector side in a hybrid query: 0 = keyword only, 1 = vector only. */
export const SEMANTIC_HYBRID_ALPHA = 0.6;
export const SEMANTIC_NEIGHBOURS = 50;
/**
 * Cosine distance above which a vector hit is not shown. A starting value: the multilingual e5
 * models score even unrelated text fairly close, so calibrate it with scripts/eval-semantic-search.ts
 * (it prints the distance of the labelled relevant hits) before turning the feature on.
 */
export const SEMANTIC_MAX_DISTANCE = 0.3;

export function isSemanticSearchEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env.SEMANTIC_SEARCH_ENABLED === "true";
}

export interface SemanticParamsInput {
  q: string;
  query_by: string;
  [key: string]: unknown;
}

/** Adds the vector side to a keyword search: the embedding field joins `query_by` and a vector query sets the blend. */
export function withHybridSearch<T extends SemanticParamsInput>(params: T, opts: { alpha?: number; k?: number } = {}): T & { vector_query: string } {
  const alpha = opts.alpha ?? SEMANTIC_HYBRID_ALPHA;
  const k = opts.k ?? SEMANTIC_NEIGHBOURS;
  const fields = params.query_by.split(",").map((f) => f.trim());
  const queryBy = fields.includes(SEMANTIC_EMBEDDING_FIELD) ? params.query_by : [...fields, SEMANTIC_EMBEDDING_FIELD].join(",");
  return { ...params, query_by: queryBy, vector_query: `${SEMANTIC_EMBEDDING_FIELD}:([], k:${k}, alpha:${alpha})` };
}

/** A pure vector query (no keyword matching) so distances compare across the three language collections. */
export function pureVectorParams(q: string, extra: Record<string, unknown> = {}, k: number = SEMANTIC_NEIGHBOURS): Record<string, unknown> {
  return { ...extra, q, query_by: SEMANTIC_EMBEDDING_FIELD, vector_query: `${SEMANTIC_EMBEDDING_FIELD}:([], k:${k})` };
}

/** Jobs nearest to one job, by its stored vector (`docId` is the Typesense document id, not the objectID). */
export function similarToDocParams(docId: string, extra: Record<string, unknown> = {}, k: number = SEMANTIC_NEIGHBOURS): Record<string, unknown> {
  const safe = docId.replace(/[^A-Za-z0-9_-]/g, "");
  return { ...extra, q: "*", vector_query: `${SEMANTIC_EMBEDDING_FIELD}:([], id:${safe}, k:${k})` };
}

/** Jobs nearest to a vector the caller already holds (the way to search the OTHER language collections for jobs like one job). */
export function similarToVectorParams(vector: readonly number[], extra: Record<string, unknown> = {}, k: number = SEMANTIC_NEIGHBOURS): Record<string, unknown> {
  const body = vector.map((v) => Number(v.toFixed(6))).join(",");
  return { ...extra, q: "*", vector_query: `${SEMANTIC_EMBEDDING_FIELD}:([${body}], k:${k})` };
}

export interface VectorHit {
  document: Record<string, unknown>;
  vector_distance?: number;
  hybrid_search_info?: { rank_fusion_score?: number };
  text_match?: number;
}

/** Smaller is closer. Hits without a vector distance (keyword-only) sort after every vector hit. */
export function distanceOf(hit: VectorHit): number {
  return typeof hit.vector_distance === "number" ? hit.vector_distance : Number.POSITIVE_INFINITY;
}

export interface MergedHit {
  collection: string;
  document: Record<string, unknown>;
  distance: number;
}

/** Merges per-collection hit lists into one list ordered by vector distance, then by original rank. */
export function mergeByDistance(lists: ReadonlyArray<{ collection: string; hits: readonly VectorHit[] }>, limit: number): MergedHit[] {
  const all: Array<MergedHit & { rank: number }> = [];
  for (const { collection, hits } of lists) {
    hits.forEach((h, rank) => all.push({ collection, document: h.document, distance: distanceOf(h), rank }));
  }
  all.sort((a, b) => (a.distance === b.distance ? a.rank - b.rank : a.distance < b.distance ? -1 : 1));
  const seen = new Set<string>();
  const out: MergedHit[] = [];
  for (const h of all) {
    const key = String(h.document.objectID ?? h.document.id ?? "");
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    out.push({ collection: h.collection, document: h.document, distance: h.distance });
    if (out.length >= limit) break;
  }
  return out;
}
